/**
 * The confirmation for deleting a Kuutio instance (#1209), shared by
 * Instanssit's archived view and Perustiedot's Vaarallinen alue. Shows the
 * dry-run row counts (DELETE ?dryRun=true), requires the title typed back,
 * then deletes. `onDeleted` runs after a successful delete.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Trash2 } from 'lucide-react';
import ModalWrapper, { ModalCard } from '../ModalWrapper';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Button, Input, StatusLine } from '../InstanceSettings/formPrimitives';

const Title = styled.h3`
  margin: 0 0 12px;
  font-size: 1.1rem;
  color: #ff8a80;
`;

const Text = styled.p`
  margin: 0 0 12px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.75);
`;

const Preview = styled.div`
  max-height: 180px;
  overflow: auto;
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);

  ul { margin: 6px 0 0; padding-left: 18px; }
`;

const Row = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

export default function DeleteInstanceDialog({ instance, onClose, onDeleted }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.INSTANCE_DELETE(instance.id, { dryRun: true }), { method: 'DELETE', credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => { if (!cancelled) setPreview(data); })
      .catch((err) => { if (!cancelled) setPreviewError(err.message); });
    return () => { cancelled = true; };
  }, [instance.id]);

  const matches = typed.trim() === String(instance.title || '').trim();

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.INSTANCE_DELETE(instance.id), { method: 'DELETE', credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.details || body?.error || `HTTP ${res.status}`);
      }
      onDeleted?.(await res.json());
    } catch (err) {
      setError(t('settingsPages.saveFailed', { error: err.message }));
      setBusy(false);
    }
  };

  return (
    <ModalWrapper visible onClose={busy ? undefined : onClose} zIndex={1100} opacity={0.85} showCloseButton={false}>
      <ModalCard size="sm" onClick={(e) => e.stopPropagation()} style={{ padding: '28px 32px 24px' }}>
        <Title>{t('instanceDelete.title')}</Title>
        <Text>{t('instanceDelete.lead', { title: instance.title })}</Text>
        <Preview>
          {previewError && <StatusLine $error>{t('instanceDelete.previewFailed', { error: previewError })}</StatusLine>}
          {!preview && !previewError && <span>{t('settingsPages.loading')}</span>}
          {preview && (
            <>
              <strong>{t('instanceDelete.previewHeading', { count: preview.totalRows })}</strong>
              <ul>
                {preview.deleted.map((d) => <li key={d.table}>{d.table}: {d.rows}</li>)}
              </ul>
            </>
          )}
        </Preview>
        <Text>{t('instanceDelete.typeTitle', { title: instance.title })}</Text>
        <Input type="text" value={typed} onChange={(e) => setTyped(e.target.value)} disabled={busy}
          placeholder={instance.title} autoFocus />
        {error && <div style={{ marginTop: 8 }}><StatusLine $error>{error}</StatusLine></div>}
        <Row>
          <Button type="button" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          <Button type="button" $variant="danger" onClick={remove} disabled={busy || !matches || !preview}>
            <Trash2 /> {busy ? t('instanceSettingsModal.deleting') : t('instanceSettingsModal.deletePermanently')}
          </Button>
        </Row>
      </ModalCard>
    </ModalWrapper>
  );
}
